import { apiErrorMessage } from '@agos/api-client'
import type { BeverageCategory } from '@agos/api-client/model'
import {
  getGetBeverageStockCategoriesQueryKey,
  getGetBeverageStockItemsQueryKey,
  getGetBeverageStockSummaryQueryKey,
  useGetBeverageStockCategories,
  usePutBeverageStockStockCategoriesId,
} from '@agos/api-client/inventory'
import { T, tt } from '@agos/shared'
import { useToast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Tags } from 'lucide-react'

import {
  BY_NAME,
  byNameLabel,
  choicePayload,
  countedFirst,
  heldLabel,
  selectedChoice,
  STOCK_CHOICES,
  stockGuess,
} from './beverage-categories'
import { serverAnswer } from './read-failure'

/**
 * *Kategórie* on *Nápojový sklad* - what counts as a drink and which tab it is in, as a choice per category,
 * with the guess it replaces named in the first option. FRONTEND.md §9.743, owner O116.
 *
 * The **stock** categories decide the table's rows and tabs, and - since §9.748 - what *Zaznamenať príjem*
 * offers, because the dialog receives the rows themselves. A choice saves at once and refreshes the reads whose
 * answers it changes.
 *
 * **The product list is gone - §9.748, owner O122.** It decided what the dialog offered while the dialog offered
 * products; it offers stock items now, so a product choice would change nothing on this screen. The server keeps
 * both lists and both writes (BACKEND.md §10A.396), so nothing stored is lost.
 *
 * **Its own read - §9.746, owner O118.** The lists came with the stock take's summary, so when that failed at
 * Sereď the editor went with it: *"I cant edit it... becuase editor will disappear"*. `GET
 * /beverage-stock/categories` answers the same two lists on their own, so a category can be set while the stock
 * take cannot be read - and a failure of this read says so here, with its own retry.
 */
export function BeverageCategoriesPanel() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data, isLoading, isError, error, refetch } = useGetBeverageStockCategories()
  const stockCategories: readonly BeverageCategory[] = data?.stockCategories ?? []

  // Returned rather than fired and forgotten: the mutation stays pending until the reads have answered, so the
  // select keeps showing the choice instead of snapping back to the old answer for the length of a refetch.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetBeverageStockCategoriesQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getGetBeverageStockSummaryQueryKey() }),
      // §9.748 - the receive dialog offers the stock take's rows, so a choice changes its list too.
      queryClient.invalidateQueries({ queryKey: getGetBeverageStockItemsQueryKey() }),
    ])
  const refused = (caught: unknown) =>
    toast({
      title: tt('Chyba'),
      description: apiErrorMessage(caught, tt('Voľbu sa nepodarilo uložiť.')),
      variant: 'destructive',
    })

  const stock = usePutBeverageStockStockCategoriesId({
    mutation: { onSuccess: refresh, onError: refused },
  })

  return (
    <details className="rounded-xl border border-border bg-card" data-beverage-categories>
      <summary className="flex cursor-pointer flex-wrap items-center gap-2 px-4 py-3 text-sm font-semibold">
        <Tags className="h-4 w-4 shrink-0 text-muted-foreground" />
        <T>Kategórie</T>
        <span className="font-normal text-muted-foreground">
          <T>čo je nápoj a do ktorej záložky patrí</T>
        </span>
      </summary>
      <div className="space-y-5 border-t border-border px-4 py-4">
        <p className="text-xs text-muted-foreground" data-beverage-categories-rule>
          <T>
            Kategóriu, ktorú nenastavíte, posúdi jej názov: nápojom je, ak obsahuje nealko, pivo, víno, nápoje,
            čapované, liehoviny alebo alko. Voľba ten odhad nahradí a platí pre všetkých.
          </T>
        </p>
        {isError ? (
          /* §9.746 - this read's own failure, said on the panel: the lists are absent, not empty, so
             *Žiadne kategórie* would be a claim about the franchise rather than about the request. */
          <div className="space-y-2 text-xs" role="alert" data-beverage-categories-failed>
            <p className="font-semibold text-red-500">
              <T>Kategórie sa nepodarilo načítať.</T>
            </p>
            <p className="text-muted-foreground" data-beverage-categories-failure-detail>
              {serverAnswer(error)}
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 font-semibold hover:bg-accent"
              data-beverage-categories-retry
            >
              <RefreshCw className="h-3.5 w-3.5" />
              <T>Skúsiť znova</T>
            </button>
          </div>
        ) : isLoading ? (
          <p className="text-xs text-muted-foreground" data-beverage-categories-loading>
            <T>Načítavam kategórie…</T>
          </p>
        ) : (
          <CategoryList
            title={tt('Skladové kategórie')}
            note={tt('Riadky tabuľky, záložky nad ňou a nápoje, ktoré ponúka Zaznamenať príjem.')}
            categories={countedFirst(stockCategories)}
            choices={STOCK_CHOICES}
            guess={stockGuess}
            pending={stock.isPending}
            saving={stock.isPending ? stock.variables : undefined}
            onChoose={(id, value) => stock.mutate({ id, data: choicePayload(value) })}
          />
        )}
      </div>
    </details>
  )
}

function CategoryList({
  title,
  note,
  categories,
  choices,
  guess,
  pending,
  saving,
  onChoose,
}: {
  title: string
  note: string
  categories: readonly BeverageCategory[]
  choices: readonly { value: string; label: string }[]
  guess: (category: BeverageCategory) => string
  pending: boolean
  /** The choice on its way to the server, shown in its select until both reads have answered. */
  saving: { id: number; data: { beverage?: string | null } } | undefined
  onChoose: (id: number, value: string) => void
}) {
  return (
    <section className="space-y-2" data-beverage-category-list="stock">
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground">{note}</p>
      </div>
      {categories.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          <T>Žiadne kategórie.</T>
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {categories.map((category) => (
            <li
              key={category.id}
              className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
              data-beverage-category={category.id}
              data-beverage-category-counted={category.counted}
            >
              <div className="min-w-0">
                <span className="break-words text-sm font-medium">{category.name}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {heldLabel(category.count)}
                </span>
              </div>
              <select
                value={
                  saving?.id === category.id ? (saving.data.beverage ?? BY_NAME) : selectedChoice(category)
                }
                onChange={(event) => onChoose(category.id, event.target.value)}
                disabled={pending}
                aria-label={`${title}: ${category.name}`}
                data-beverage-category-choice
                className="max-w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50"
              >
                <option value={BY_NAME}>{byNameLabel(guess(category))}</option>
                {choices.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
